// /////////////////////////////////////////////////////////////
// [ファイル] SalesProjectsFilterList.tsx
// [概要] 案件条件リスト（保存済み検索条件の一覧）。
//        2026-11-05 に Firestore から Cloud SQL（useSfaSavedFilters）へ移行し、
//        個人／指名共有の区分を追加。
//
// 画面の決まり
// - 「自分の条件」と「共有された条件」の 2 区分で並べる
// - 作成者の氏名を常に表示する。退職していれば「（退職）」を付ける
// - 編集・削除は作成者のみ。共有された条件は「複製」で自分の条件にできる
// - 作成者が退職した共有条件は、共有メンバーが「引き継ぐ」で作成者になれる
// - 管理者（全社閲覧権限）は共有条件すべてを見られ、削除と引き継ぎができる
// /////////////////////////////////////////////////////////////
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Grid,
  Fab,
  Chip,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from "../constants/icons";
import SalesProjectSearchFilters from '../components/SalesProjectSearchFilters';
import LoadingView from '../components/LoadingView';
import { ProjectFilters, SortConfig, DEFAULT_FILTERS } from '../hooks/useProjectTable';
import { SELECTABLE_PHASES } from '../hooks/useSfaStructure';
import {
  SfaSavedFilter,
  sfaSavedFilterApi,
  isSavedFilterAdmin,
  formatSavedFilterDate,
} from '../hooks/useSfaSavedFilters';
import { DEFAULT_LIST_COLUMNS, SFA_SORT_OPTIONS, isDefaultListColumns, normalizeListColumns } from '../components/sfa/sfaListColumns';
import { PROJECT_TYPE_LABELS } from '../constants/masterData';
import { STANDARD_FAB_CTA_SX } from '../constants/buttonStyles';
import { useAuth } from "../firebase/AuthProvider";
import { usePermissions } from '../firebase/PermissionProvider';
import { useNavigate } from 'react-router-dom';
import { useConfirm } from '../providers/ConfirmProvider';
import { useNotification } from '../providers/NotificationProvider';

export default function SalesProjectsFilterList() {
  const { user } = useAuth();
  const { permissionData, loading: permissionLoading } = usePermissions();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const notify = useNotification();
  const [savedFilters, setSavedFilters] = useState<SfaSavedFilter[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [openSearch, setOpenSearch] = useState(false);
  const [editingFilter, setEditingFilter] = useState<SfaSavedFilter | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  // デフォルトのフィルタとソート
  const defaultFilters: ProjectFilters = DEFAULT_FILTERS;
  const defaultSort: SortConfig = { field: 'phase', order: 'asc' };

  const isAdmin = isSavedFilterAdmin(permissionData);
  const viewLevel = permissionData.includes('readdata.sfa.all_view')
    ? 'all'
    : permissionData.includes('readdata.sfa.branch_view')
      ? 'branch'
      : 'individual';

  const fetchSavedFilters = useCallback(async () => {
    if (!user?.email) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await sfaSavedFilterApi.list(user.email, isAdmin);
      setSavedFilters(res.rows);
    } catch (error: any) {
      console.error("Error fetching saved filters:", error);
      setLoadError(error?.message || '保存済み条件の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [user?.email, isAdmin]);

  // 権限の読み込みが終わってから取得する（管理者かどうかで一覧が変わる）
  useEffect(() => {
    if (permissionLoading) return;
    fetchSavedFilters();
  }, [permissionLoading, fetchSavedFilters]);

  const handleDelete = async (filter: SfaSavedFilter, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user?.email) return;
    const ok = await confirm({
      title: '条件の削除',
      message: filter.is_owner
        ? `「${filter.name}」を削除してもよろしいですか？${filter.scope === 'shared' ? '共有メンバーからも見えなくなります。' : ''}`
        : `管理者として「${filter.name}」（作成者 ${filter.creator_name}）を削除します。よろしいですか？`,
      confirmText: '削除する',
      confirmIcon: <Icons.Delete />,
      destructive: true,
    });
    if (!ok) return;

    setBusyId(filter.id);
    try {
      await sfaSavedFilterApi.remove(user.email, filter.id, isAdmin);
      setSavedFilters(prev => prev.filter(f => f.id !== filter.id));
      notify.success('条件を削除しました。');
    } catch (error: any) {
      console.error("Error deleting filter:", error);
      notify.error(error?.message || "削除に失敗しました。");
    } finally {
      setBusyId(null);
    }
  };

  const handleEdit = (filter: SfaSavedFilter, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingFilter(filter);
    setOpenSearch(true);
  };

  // 共有された条件を自分の個人条件として複製する（編集は作成者のみのため）
  const handleDuplicate = async (filter: SfaSavedFilter, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user?.email) return;
    setBusyId(filter.id);
    try {
      await sfaSavedFilterApi.save(user.email, {
        name: `${filter.name} (複製)`,
        filters: filter.filters,
        sort: filter.sort,
        ...(filter.columns ? { columns: filter.columns } : {}),
        scope: 'personal',
        shared_with: [],
      });
      notify.success('自分の条件として複製しました。');
      await fetchSavedFilters();
    } catch (error: any) {
      console.error("Error duplicating filter:", error);
      notify.error(error?.message || '複製に失敗しました。');
    } finally {
      setBusyId(null);
    }
  };

  // 作成者が退職した共有条件を引き継ぐ（管理者はいつでも可）
  const handleTakeOver = async (filter: SfaSavedFilter, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user?.email) return;
    const ok = await confirm({
      title: '条件の引き継ぎ',
      message: `「${filter.name}」（作成者 ${filter.creator_name}）を引き継いで、自分が作成者になります。以後は編集・削除ができます。よろしいですか？`,
      confirmText: '引き継ぐ',
      confirmIcon: <Icons.SwapHoriz />,
    });
    if (!ok) return;
    setBusyId(filter.id);
    try {
      await sfaSavedFilterApi.takeOver(user.email, filter.id, isAdmin);
      notify.success('条件を引き継ぎました。');
      await fetchSavedFilters();
    } catch (error: any) {
      console.error("Error taking over filter:", error);
      notify.error(error?.message || '引き継ぎに失敗しました。');
    } finally {
      setBusyId(null);
    }
  };

  // 統合版（/sfa/projects）で開く。移行期間中は旧画面でも開ける
  const handleApply = (filter: SfaSavedFilter, legacy = false) => {
    navigate(legacy ? '/salesprojectsmanagement' : '/sfa/projects', {
      state: {
        appliedFilters: filter?.filters || {},
        appliedSort: filter?.sort || (legacy ? { field: 'phase', order: 'asc' } : { field: 'updated_date', order: 'desc' }),
        appliedFilterName: filter?.name,
        ...(!legacy && filter?.columns ? { appliedColumns: filter.columns } : {}),
      }
    });
  };

  if (loading || permissionLoading) {
    return <LoadingView message="保存済み条件を取得中..." height="80vh" />;
  }

  const myFilters = savedFilters.filter((f) => f.is_owner);
  const sharedFilters = savedFilters.filter((f) => !f.is_owner);

  const memberLabel = (m: { name: string; retired: boolean }) => (m.retired ? `${m.name}（退職）` : m.name);

  const renderCard = (filter: SfaSavedFilter) => {
    const busy = busyId === filter.id;
    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={filter.id}>
        <Card
          sx={{
            cursor: 'pointer',
            transition: '0.2s',
            '&:hover': {
              boxShadow: 4,
              transform: 'translateY(-2px)'
            },
            position: 'relative',
            opacity: busy ? 0.6 : 1,
            ...(filter.is_owner ? {} : { borderLeft: '3px solid #94a3b8' }),
          }}
          onClick={() => handleApply(filter)}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                {filter.name || '無題の条件'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                {filter.can_edit && (
                  <Tooltip title="編集">
                    <IconButton
                      size="small"
                      disabled={busy}
                      onClick={(e) => handleEdit(filter, e)}
                      sx={{ color: '#94a3b8', '&:hover': { color: '#607d8b' } }}
                    >
                      <Icons.Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {!filter.is_owner && (
                  <Tooltip title="作成者のみ編集できます。複製すると自分の条件として編集できます">
                    <span>
                      <IconButton
                        size="small"
                        disabled={busy}
                        onClick={(e) => handleDuplicate(filter, e)}
                        sx={{ color: '#94a3b8', '&:hover': { color: '#607d8b' } }}
                      >
                        <Icons.ContentCopy fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {filter.can_take_over && (
                  <Tooltip title={filter.creator_retired ? '作成者が退職しているため引き継げます' : '管理者として引き継ぎます'}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={busy}
                        onClick={(e) => handleTakeOver(filter, e)}
                        sx={{ color: '#f59e0b', '&:hover': { color: '#d97706' } }}
                      >
                        <Icons.SwapHoriz fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {filter.can_delete && (
                  <Tooltip title={filter.is_owner ? '削除' : '管理者として削除'}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={busy}
                        onClick={(e) => handleDelete(filter, e)}
                        sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                      >
                        <Icons.Delete fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {/* 作成者と共有区分（誰が編集できるかが一目で分かるように常に出す） */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
              <Tooltip title={filter.transferred_from ? `${filter.transferred_from} から引き継ぎ（${formatSavedFilterDate(filter.transferred_at)}）` : '編集・削除ができるのは作成者のみです'}>
                <Chip
                  icon={<Icons.Person sx={{ fontSize: 14 }} />}
                  label={`作成者 ${filter.creator_name}${filter.creator_retired ? '（退職）' : ''}`}
                  size="small"
                  variant="outlined"
                  sx={filter.creator_retired
                    ? { borderColor: '#ef4444', color: '#b91c1c', '& .MuiChip-icon': { color: '#b91c1c' } }
                    : { borderColor: '#cbd5e1', color: '#475569', '& .MuiChip-icon': { color: '#64748b' } }}
                />
              </Tooltip>
              {filter.scope === 'shared' ? (
                <Tooltip title={`共有メンバー: ${filter.shared_with.map(memberLabel).join('、')}`}>
                  <Chip
                    icon={<Icons.Groups sx={{ fontSize: 14 }} />}
                    label={`共有 ${filter.shared_with.length}人`}
                    size="small"
                    color="primary"
                    variant={filter.is_owner ? 'filled' : 'outlined'}
                  />
                </Tooltip>
              ) : (
                <Chip icon={<Icons.Lock sx={{ fontSize: 14 }} />} label="個人" size="small" variant="outlined" sx={{ borderColor: '#cbd5e1', color: '#64748b', '& .MuiChip-icon': { color: '#94a3b8' } }} />
              )}
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
              {filter.filters?.project_name && (
                <Chip label={`KW: ${filter.filters.project_name}`} size="small" variant="outlined" />
              )}
              {filter.filters?.phase && filter.filters.phase.length > 0 && (
                <Chip
                  label={`フェーズ: ${Array.isArray(filter.filters.phase) ? filter.filters.phase.join(', ') : filter.filters.phase}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              {filter.filters?.project_type && filter.filters.project_type.length > 0 && (
                <Chip
                  label={`種別: ${filter.filters.project_type.map((t: string) => PROJECT_TYPE_LABELS[t] ?? t).join(', ')}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              {filter.filters?.main_staff_branch_name && filter.filters.main_staff_branch_name.length > 0 && (
                <Chip
                  label={`支店: ${Array.isArray(filter.filters.main_staff_branch_name) ? filter.filters.main_staff_branch_name.join(', ') : filter.filters.main_staff_branch_name}`}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: '#64748b', color: '#64748b' }}
                />
              )}
              {filter.filters?.main_staff_department_name && filter.filters.main_staff_department_name.length > 0 && (
                <Chip
                  label={`部署: ${Array.isArray(filter.filters.main_staff_department_name) ? filter.filters.main_staff_department_name.join(', ') : filter.filters.main_staff_department_name}`}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: '#64748b', color: '#64748b' }}
                />
              )}
              {filter.filters?.main_staff_name && (
                <Chip label={`メイン担当者: ${filter.filters.main_staff_name}`} size="small" variant="outlined" />
              )}
              {filter.filters?.category_large && (
                <Chip label={filter.filters.category_large} size="small" variant="outlined" />
              )}
              {filter.sort && (
                <Chip
                  label={`ソート: ${SFA_SORT_OPTIONS.find((o) => o.value === filter.sort?.field)?.label || filter.sort.field} (${filter.sort.order === 'asc' ? '昇順' : '降順'})`}
                  size="small"
                  sx={{ bgcolor: '#f1f5f9' }}
                />
              )}
              {filter.columns && !isDefaultListColumns(normalizeListColumns(filter.columns)) && (
                <Chip label={`表示列: ${normalizeListColumns(filter.columns).length} 列`} size="small" sx={{ bgcolor: '#f1f5f9' }} />
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                保存日: {formatSavedFilterDate(filter.created_at)}
              </Typography>
              <Typography
                variant="caption" component="button"
                onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleApply(filter, true); }}
                sx={{ color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', p: 0, '&:hover': { color: '#607d8b' } }}
              >
                旧画面で開く
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const sectionTitle = (icon: React.ReactNode, label: string, count: number, hint: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mt: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: blueGrey[700], display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {icon} {label}
      </Typography>
      <Chip label={`${count} 件`} size="small" sx={{ bgcolor: '#f1f5f9', height: 20, fontSize: '0.7rem' }} />
      <Typography variant="caption" sx={{ color: '#94a3b8' }}>{hint}</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 3, width: '95%', margin: '0 auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 2, color: blueGrey[800] }}>
          <Icons.FactCheck /> 保存済み検索条件一覧
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Fab
            variant="extended"
            onClick={() => setOpenSearch(true)}
            sx={[STANDARD_FAB_CTA_SX, { mr: 3 }]}
          >
            <Icons.Add />
            新規条件作成
          </Fab>
          <Fab
            variant="extended"
            onClick={() => navigate('/salesprojectsmanagement')}
            sx={STANDARD_FAB_CTA_SX}
          >
            <Icons.ArrowBack />
            案件リスト
          </Fab>
        </Box>
      </Box>

      {/* 新規条件作成・編集ダイアログ */}
      <Dialog
        open={openSearch}
        onClose={() => {
          setOpenSearch(false);
          setEditingFilter(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle component="div" sx={{ bgcolor: '#f1f5f9', p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#334155', fontSize: '14px' }}>
            {editingFilter ? `検索条件の編集: ${editingFilter.name}` : '新規検索条件の作成'}
          </Typography>
          <IconButton size="small" onClick={() => { setOpenSearch(false); setEditingFilter(null); }}>
            <Icons.Close sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <SalesProjectSearchFilters
            filters={editingFilter ? editingFilter.filters : defaultFilters}
            sort={editingFilter?.sort ?? defaultSort}
            viewLevel={viewLevel}
            phaseOptions={SELECTABLE_PHASES} // 条件は統合版で開くため、フェーズの選択肢も統合版に合わせる (販売前の仕入案件は仕入フェーズで判定)
            sortOptions={SFA_SORT_OPTIONS}
            columns={editingFilter?.columns ? normalizeListColumns(editingFilter.columns) : [...DEFAULT_LIST_COLUMNS]}
            savedFilterId={editingFilter?.id}
            initialFilterName={editingFilter?.name}
            initialScope={editingFilter?.scope}
            initialSharedWith={editingFilter?.shared_with.map((m) => m.user_id)}
            onSearch={(newFilters) => {
              setOpenSearch(false);
              navigate('/sfa/projects', {
                state: {
                  appliedFilters: newFilters,
                  appliedSort: { field: 'updated_date', order: 'desc' }
                }
              });
            }}
            onSortChange={() => { }}
            onSaveSuccess={() => {
              fetchSavedFilters();
              setOpenSearch(false);
              setEditingFilter(null);
            }}
            showSearchButton={false}
            showResetButton={false}
          />
        </DialogContent>
      </Dialog>

      {loadError && (
        <Card sx={{ textAlign: 'center', py: 4, mb: 2, bgcolor: '#fef2f2', border: '1px dashed #fca5a5' }}>
          <Typography color="error">{loadError}</Typography>
        </Card>
      )}

      {savedFilters.length === 0 && !loadError ? (
        <Card sx={{ textAlign: 'center', py: 8, bgcolor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
          <Icons.FilterList sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
          <Typography color="text.secondary">
            保存された検索条件はありません。<br />
            案件一覧の検索画面から条件を保存できます。
          </Typography>
        </Card>
      ) : (
        <>
          {sectionTitle(<Icons.Person sx={{ fontSize: 18 }} />, '自分の条件', myFilters.length, '編集・削除ができます。共有にすると指名したメンバーにも表示されます')}
          {myFilters.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3, ml: 1 }}>自分が作成した条件はありません。</Typography>
          ) : (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {myFilters.map(renderCard)}
            </Grid>
          )}

          {(sharedFilters.length > 0 || isAdmin) && (
            <>
              {sectionTitle(<Icons.Groups sx={{ fontSize: 18 }} />, '共有された条件', sharedFilters.length,
                isAdmin ? '管理者のため全員の共有条件が表示されます。適用・複製のほか、削除と引き継ぎができます' : '適用と複製ができます。編集・削除は作成者のみです')}
              {sharedFilters.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#94a3b8', ml: 1 }}>共有された条件はありません。</Typography>
              ) : (
                <Grid container spacing={2}>
                  {sharedFilters.map(renderCard)}
                </Grid>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
}
